import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertTriangle, Users } from 'lucide-react';

import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { createGroup } from '@/lib/firebase/queries/groups';
import { getGroupHierarchy } from '@/lib/firebase/queries/hierarchical-groups';
import { localizedName } from '@/lib/groups/groupPosts';

import type { Group } from '@/types/support';
import { toast } from 'sonner';

interface AddGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  organizationId: string;
}

export function AddGroupDialog({ open, onOpenChange, onSuccess, organizationId }: AddGroupDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentGroupId, setParentGroupId] = useState<string>('');
  const [groupType, setGroupType] = useState<'parent' | 'subgroup' | ''>('');
  const [availableGroups, setAvailableGroups] = useState<(Group & { children: Group[]; level?: number })[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const { t, dir, language } = useLanguage();
  const { user, userData } = useAuth();

  useEffect(() => {
    if (open && organizationId) {
      fetchAvailableGroups();
    }
  }, [open, organizationId]);

  const fetchAvailableGroups = async () => {
    try {
      const hierarchy = await getGroupHierarchy(organizationId);
      const flatGroups = flattenHierarchy(hierarchy, 0);
      
      setAvailableGroups(flatGroups);
    } catch (error) {
      console.error('Error fetching available groups:', error);
    }
  };

  const flattenHierarchy = (nodes: (Group & { children: Group[] })[], level: number): (Group & { children: Group[]; level: number })[] => {
    const result: (Group & { children: Group[]; level: number })[] = [];
    for (const node of nodes) {
      result.push({ ...node, level });
      result.push(...flattenHierarchy(node.children, level + 1));
    }
    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !user) return;
    
    // Validation: Ensure a type is selected
    if (!groupType) {
      toast.error(t('dashboard.manager.groups.typeRequired'));
      return;
    }
    
    // Validation: For subgroups, ensure a valid section is selected
    if (groupType === 'subgroup' && (!parentGroupId || parentGroupId === 'no-sections')) {
      toast.error(t('dashboard.manager.groups.sectionRequired'));
      return;
    }

    try {
      setIsLoading(true);


      const success =       await createGroup({
        name: name.trim(),
        description: description.trim() || '',
        categories: [],
        isPrivate: false,
        creatorId: user.uid,
        // `user` can be a bare { uid } (phone sign-in): name from the profile.
        creatorName: localizedName(userData, language, user.displayName || ''),
        organization: organizationId,
        parentGroupId: groupType === 'parent' 
          ? undefined // Parent groups are always top-level
          : parentGroupId, // Subgroups must have a valid parent section
        groupType: groupType as 'parent' | 'subgroup'
      });
      
      if (success) {
        setName('');
        setDescription('');
        setParentGroupId(''); // Reset to empty to force selection
        setGroupType(''); // Reset to empty to force selection

        toast.success(t('dashboard.manager.groups.createSuccess'));
        onSuccess?.();
        onOpenChange(false);
      } else {
        // createGroup reports failure as null, not a throw.
        toast.error(t('dashboard.manager.groups.createError'));
      }
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error(t('dashboard.manager.groups.createError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]" dir={dir}>
        <DialogHeader>
          <DialogTitle>{t('dashboard.manager.groups.addGroup')}</DialogTitle>
          <DialogDescription>
            {t('dashboard.manager.groups.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">{t('dashboard.manager.groups.table.name')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('dashboard.manager.groups.table.name')}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('dashboard.manager.groups.description')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('dashboard.manager.groups.description')}
              className="min-h-[100px]"
              disabled={isLoading}
            />
          </div>

          {/* First Dropdown: Group Type */}
          <div className="space-y-2">
            <Label htmlFor="groupType">{t('dashboard.manager.groups.groupType')}</Label>
            <Select 
              value={groupType} 
              onValueChange={(value: string) => {
                const selectedType = value as 'parent' | 'subgroup';
                setGroupType(selectedType);
                // Auto-set parent for parent groups
                if (selectedType === 'parent') {
                  setParentGroupId('top-level');
                } else {
                  // Reset to empty for subgroups to force section selection
                  setParentGroupId('');
                }
              }} 
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('dashboard.manager.groups.selectGroupType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="parent">
                  {t('dashboard.manager.groups.section')}
                </SelectItem>
                <SelectItem value="subgroup">
                  {t('dashboard.manager.groups.group')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Second Dropdown: Parent Section (only for subgroups) */}
          {groupType === 'subgroup' && (
            <div className="space-y-2 mt-3">
              <Label htmlFor="parentGroup">{t('dashboard.manager.groups.selectSection')}</Label>
              <Select value={parentGroupId} onValueChange={setParentGroupId} disabled={isLoading}>
                <SelectTrigger>
                  <SelectValue placeholder={t('dashboard.manager.groups.selectSectionPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {availableGroups
                    .filter(group => group.groupType === 'parent') // Only show parent groups as options
                    .map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  {availableGroups.filter(group => group.groupType === 'parent').length === 0 && (
                    <SelectItem value="no-sections" disabled>
                      {t('dashboard.manager.groups.noSectionsAvailable')}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={
                isLoading || 
                !name.trim() || 
                !groupType ||
                (groupType === 'subgroup' && !parentGroupId) // Require parent selection for subgroups
              }
              className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                t('common.save')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 